import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, Link } from "react-router-dom";
import QRCode from "qrcode";
import { api } from "../lib/api";

interface Station {
  id: string;
  code: string;
  type: string;
  position_description: string | null;
}

export function QRPrintSheet() {
  const { t } = useTranslation();
  const { locationId } = useParams<{ locationId: string }>();
  const [location, setLocation] = useState<any>(null);
  const [stations, setStations] = useState<Station[]>([]);
  const [qrCodes, setQrCodes] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const [loc, stns] = await Promise.all([
          api(`/locations/${locationId}`),
          api(`/stations?locationId=${locationId}`),
        ]);
        setLocation(loc);
        setStations(stns);

        const codes: Record<string, string> = {};
        for (const s of stns) {
          const url = `${window.location.origin}/app/inspect/${s.id}`;
          codes[s.id] = await QRCode.toDataURL(url, {
            width: 200,
            margin: 1,
            color: { dark: "#0c1222", light: "#ffffff" },
          });
        }
        setQrCodes(codes);
      } catch {
        // handled by empty state
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [locationId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-navy-200 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!location) {
    return (
      <div className="animate-fade-in text-center py-20">
        <p className="text-navy-500">{t("common.error")}</p>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {/* Header — hidden on print */}
      <div className="no-print mb-6">
        <Link to={`/app/clients`} className="inline-flex items-center gap-1.5 text-sm text-navy-400 hover:text-navy-600 transition-colors mb-4">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
          </svg>
          {t("common.back")}
        </Link>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
              {t("locations.qrTitle", { location: location.name })}
            </h1>
            <p className="text-sm text-navy-400 mt-1">{t("locations.qrSubtitle")}</p>
          </div>
          <button onClick={() => window.print()} className="btn btn-primary">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0l.229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 011.913-.247m10.5 0a48.536 48.536 0 00-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18 10.5h.008v.008H18V10.5zm-3 0h.008v.008H15V10.5z" />
            </svg>
            {t("haccp.printPdf")}
          </button>
        </div>
      </div>

      {/* Print-only header */}
      <div className="hidden print:block mb-6">
        <h1 className="text-xl font-bold">{location.name} — {location.client_name}</h1>
        <p className="text-sm text-gray-500">{location.address}</p>
      </div>

      {/* QR grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 qr-sheet">
        {stations.map((station) => (
          <div key={station.id} className="qr-card bg-white rounded-xl border border-navy-100 p-4 text-center">
            {qrCodes[station.id] && (
              <img src={qrCodes[station.id]} alt={station.code} className="w-36 h-36 mx-auto mb-3" />
            )}
            <div className="bg-navy-900 text-amber-400 font-mono text-lg font-bold rounded-lg py-1.5 mb-2">
              {station.code}
            </div>
            <p className="text-xs text-navy-600 font-medium">
              {t(`locations.stationTypes.${station.type}`)}
            </p>
            {station.position_description && (
              <p className="text-xs text-navy-400 mt-0.5 truncate">{station.position_description}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
